import { NoneCodec } from "../codecs/none-codec.js";
import { createSpline } from "./helpers/create-spline.js";
import { getDomain } from "./helpers/data-lookup.js";
import { getValue } from "./helpers/get-value.js";
import { getSplineTimeRange, mergeSplines, } from "../spline.js";
import { Percentile } from "../../util/percentile.js";
/**
 * Returns a signal based on given parameters
 * @param time A getter that returns the timestamps for data given by the data getter
 * @param data A getter that returns the data represented by the signal
 * @param averageDataInterval The average data interval
 * @param codec The codec that can interpret the data
 * @param sourceUnit the unit of the underlying data
 * @param targetUnit the unit you want to work with (may be the same as the source unit)
 * @returns The signal that wraps the data
 */
export const signal = (time, data, averageDataInterval, codec = NoneCodec.get(), sourceUnit, targetUnit) => {
    return new Signal(time, data, averageDataInterval, codec, sourceUnit, targetUnit);
};
/**
 * Things this class should be able to facilitate
 * -graphs
 * -uniform data lists
 * -value for t
 * -down sampling
 * -detect thresholds
 */
export class Signal {
    time;
    data;
    averageDataInterval;
    codec;
    sourceUnit;
    targetUnit;
    alternatives;
    /**
     * Return a Signal object
     * @param time function that returns the timestamp of the datum at a given index
     * @param data function that returns the datum at a given index
     * @param averageDataInterval  the average interval between the data in the underlying data set
     * @param codec the codec to use for this signal. Is used for preprocessing  during interpolation.
     * @param sourceUnit the unit of the underlying data
     * @param targetUnit the unit you want to work with (may be the same as the source unit)
     */
    constructor(time, data, averageDataInterval, codec = NoneCodec.get(), sourceUnit, targetUnit) {
        this.time = time;
        this.data = data;
        this.averageDataInterval = averageDataInterval;
        this.codec = codec;
        this.sourceUnit = sourceUnit;
        this.targetUnit = targetUnit;
        if (!targetUnit.equals(sourceUnit)) {
            this.codec.setUnit(targetUnit, sourceUnit);
        }
        this.alternatives = [];
    }
    interval() {
        return this.averageDataInterval;
    }
    symbol() {
        return this.targetUnit.getSymbol();
    }
    setAlternatives(alternatives) {
        if (alternatives) {
            this.alternatives = alternatives;
        }
    }
    /**
     *
     * @returns Returns the minimum and maximum value found in the signal
     */
    extent() {
        //consult the codec for a mandatory domain
        const mandatoryDomain = this.codec.domain();
        //when no mandatory domain is defined, search within the data
        return mandatoryDomain ? mandatoryDomain : getDomain(this.data);
    }
    /**
     * Returns an iterator that return a signal value for each timestamp defined by the
     * given metronome
     * @param timing A metronome describing for which timestamps to get signal values for.
     * @param alternatives Optional signals to use when this signal has no value for a given t by the metronome
     * @returns Iterable the iterates over the signal values for given metronome
     */
    sample(timing) {
        const self = this;
        const targetInterval = timing.interval();
        let from = 0;
        return {
            *[Symbol.iterator]() {
                for (const milli of timing) {
                    const result = self._get(milli, from, targetInterval);
                    from = result.from;
                    //          console.log(`t=${t} result.from=${result.from} result.value=${result.value}`);
                    yield result.value;
                }
            },
        };
    }
    /**
     * Returns the value for the signal at t. When the signal is
     * is not defined for t, null gets returned.
     * @param milli  the timestamp (epoch milliseconds) where to get the value for.
     * @param alternatives Optional signals to use when this signal has no value for given t
     * @param timingPrecision signal starting and ending points will be snapped to this precision. Defaults to averageDataInterval / 10.0
     * @returns The value for this signal at t or null when the signal is not defined for t.
     */
    get(milli, timingPrecision) {
        let targetInterval = timingPrecision !== undefined ? timingPrecision : this.averageDataInterval * 0.1;
        return this._get(milli, 0, targetInterval).value;
    }
    /**
     * Returns the percentile for given p.
     * @param p The requested percentile p is on interval[0.0, 1.0]
     * @param metronome The time range and step size
     * @param timingPrecision signal starting and ending points will be snapped to this precision. Defaults to averageDataInterval / 10.0 (optional)
     * @returns the percentile
     */
    percentile(p, metronome, timingPrecision) {
        const n = metronome.count();
        const percentileSketch = new Percentile(p, n);
        for (let t of metronome) {
            let value = this.get(t, timingPrecision);
            if (value != null) {
                percentileSketch.add(value);
            }
        }
        return percentileSketch.get();
    }
    /**
     * Private internal function for getting a value
     * @param milli
     * @param signals
     * @param targetInterval
     * @returns
     */
    _get(milli, from, targetInterval) {
        const gapDetectionThreshold = this.averageDataInterval * 2;
        return getValue(milli, this.time, this.data, this.codec, this.averageDataInterval, gapDetectionThreshold, targetInterval, from, this.alternatives);
    }
    /**
     * Construct a Path that represent this signal between given clipsStart and
     * clipEnd. The signal is  cut at each given threshold (if provided) and detected gaps (hence
     * an array is returned). The optional timingPrecision is used for aligning gap starts and
     * endings. When absent, the native signal interval  * 0.1 is used. When a gap has been detected, the
     * fallbackSignals will be used for fallback values. When no fallback value exists, the signal
     * has a gap.
     * @param clipStart The start timestamp where to get PathSection for
     * @param clipEnd  The end timestamp where to get PathSection for
     * @param timingPrecision Gaps will be aligned to this value
     */
    spline(clipStart, clipEnd, timingPrecision) {
        const gapDetectionThreshold = this.averageDataInterval * 2;
        const targetInterval = timingPrecision != null
            ? timingPrecision
            : this.averageDataInterval * 0.1;
        const spline = createSpline(this.time, this.data, this.codec, gapDetectionThreshold, targetInterval);
        const actualTimeRange = getSplineTimeRange(spline);
        const start = clipStart !== undefined
            ? clipStart
            : actualTimeRange
                ? actualTimeRange.s
                : undefined;
        const end = clipEnd !== undefined
            ? clipEnd
            : actualTimeRange
                ? actualTimeRange.e
                : undefined;
        const self = this;
        if (start !== undefined && end !== undefined) {
            const splines = {
                *[Symbol.iterator]() {
                    yield { get: () => spline };
                    if (self.alternatives && self.alternatives.length > 0) {
                        for (let signal of self.alternatives) {
                            yield {
                                get: () => createSpline(signal.time, signal.data, signal.codec, signal.averageDataInterval * 2, targetInterval),
                            };
                        }
                    }
                },
            };
            mergeSplines(splines, start, end);
        }
        return spline;
    }
}
